import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcRaglV1ManualEntryQueryServiceManualEntryGet } from '@interxfi/api-client/generated/hooks/ixc-ragl-v1-manual-entry-query-service-manual-entry-get';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxcRaglV1ManualEntryState } from '@interxfi/types';
import { RaglManualEntryEntityMenu } from '../../../../../../../../../features/clearing-ledger/manual-entry/ragl-manual-entry-entity-menu';
import { RaglManualEntryEventSummary } from '../../../../../../../../../features/clearing-ledger/manual-entry/ragl-manual-entry-event';
import { getRaglManualEntryStateListItems } from '../../../../../../../../../features/clearing-ledger/manual-entry/ragl-manual-entry-list-items';
import { getRaglManualEntryPath } from '../../../../../../../../../features/clearing-ledger/manual-entry/ragl-manual-entry-routing';
import { RaglManualEntryStatusPill } from '../../../../../../../../../features/clearing-ledger/manual-entry/ragl-manual-entry-status-pill';
import { RaglTransactionDeltasTable } from '../../../../../../../../../features/clearing-ledger/transaction/ragl-transaction-deltas-table';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/operations/_operations/journal/_manual-entry/$entryId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `ragl-manual-entry-${params.entryId}`,
        label: 'Manual Entry',
        to: getRaglManualEntryPath({ kind: 'detail', keys: { entryId: params.entryId } }),
        suffix: <CopyButton content={params.entryId} label="Copy Entry ID" tooltip={{ copy: params.entryId, copied: 'Copied Entry ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Manual Entry'),
  component: AccountingManualEntryDetail,
});

function getListItems(data: IxcRaglV1ManualEntryState | undefined) {
  const items = getRaglManualEntryStateListItems(data);

  return {
    left: [
      items.data.effectiveAt,
      items.data.description,
      ...(items.data.reversedDetails
        ? [
            { ...items.data.reversedDetails.effectiveAt, title: 'Reverse Effective At' },
            { ...items.data.reversedDetails.reversedPostedTimestamp, title: 'Reverse Posted Timestamp' },
            ...(items.data.reversedDetails.reason ? [{ ...items.data.reversedDetails.reason, title: 'Reverse Reason' }] : []),
          ]
        : []),
    ] as const satisfies ReadonlyListItem[],
    right: [
      items.metadata.createdAt,
      items.metadata.updatedAt,
      items.entryId,
      items.data.transactionId,
      ...(items.data.reversedDetails
        ? [
            {
              ...items.data.reversedDetails.transactionId,
              title: 'Reversing Transaction ID',
            },
          ]
        : []),
    ] as const satisfies ReadonlyListItem[],
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load entry', 'view this manual entry');

function AccountingManualEntryDetail() {
  const { entryId } = Route.useParams();
  const request = { entryId } as const;
  const { data, error, isPending } = useIxcRaglV1ManualEntryQueryServiceManualEntryGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.manualEntry), [data?.manualEntry]);

  return (
    <PageLayout gap={DEFAULT_PAGE_LAYOUT_GAP} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack blockAlign="center" gap={2} wrap="nowrap">
            <RaglManualEntryStatusPill isPending={showPending} size="large" status={data?.manualEntry?.status} />
          </HorizontalStack>

          <RaglManualEntryEntityMenu {...request} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <RaglManualEntryEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3 lineHeight="1rem">Deltas</Typography.Level3>

          <RaglTransactionDeltasTable assets={data?.manualEntry?.data?.assets || []} showSkeleton={showPending} />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
